


























































































































































































































































import { Component, Vue, Prop, Ref, Watch } from 'vue-property-decorator';
import { Storage } from 'aws-amplify';
import { User } from '@/models/user';
import { State } from 'vuex-class';

declare global {
  interface Window {
    Plotly?: any;
  }
}

/* ========= Types ========= */
type Num = number;

interface Pt {
  x: number;
  y: number;
}

interface Kpi {
  motion: Num;
  structure: Num;
  keypoints: Num;
  flow: Num;
  orient: Num;
}

interface Photo {
  key: string;
  time: string | number | Date;
  id?: string;
  movement?: Num;
  score?: Num;
  kpi?: Partial<Kpi>;
  motiondetect?: Partial<Kpi> & { score?: Num };
  motionDetect?: Partial<Kpi> & { score?: Num };
}

interface Pair {
  keyA: string;
  keyB: string;
  photoBId: string;
  tB: number;
}

interface Result {
  id: string;
  keyA: string;
  keyB: string;
  tB: number;
  movement: Num;
  kpi: Kpi;
  debug?: unknown;
}

/* ========= Utils ========= */
function clamp01(x: number): number {
  return Math.max(0, Math.min(1, x));
}
function round3(x: number): number {
  return Math.round(x * 1000) / 1000;
}

/** Retire tous les 'public/' en tête */
function stripPublic(k: string | null | undefined): string {
  const s = String(k || '').replace(/^\/+/, '');
  return s.replace(/^(?:public\/)+/i, '');
}

/** Pour l'API (pas pour Amplify.get) */
function ensurePublicPrefix(k: string | null | undefined): string | null | undefined {
  if (!k) return k;
  if (/^https?:\/\//i.test(k)) return k;
  return `public/${stripPublic(k)}`;
}

function isAbsoluteUrl(v: unknown): boolean {
  return /^https?:\/\//i.test(String(v ?? ''));
}

/** Variante vignette: "nom.jpg-small" */
function toSmallAfterExt(key: string): string {
  const clean = stripPublic(key).split('?')[0];
  if (/-small$/i.test(clean)) return clean;
  const normalized = clean.replace(/-small(\.[a-z0-9]+)$/i, '$1-small');
  const m = normalized.match(/(.+?)(\.[a-z0-9]+)$/i);
  if (m) return `${m[1]}${m[2]}`;
  return `${normalized}`;
}

/** Ancien format: "nom-small.jpg" */
function toSmallPreExt(key: string): string {
  const clean = stripPublic(key).split('?')[0];
  if (/-small(\.[a-z0-9]+)$/i.test(clean)) return clean;
  const m = clean.match(/(.+?)(\.[a-z0-9]+)$/i);
  if (m) return `${m[1]}-small${m[2]}`;
  return `${clean}-small`;
}

function safeNum(v: any, def = 0): number {
  if (v === null || v === undefined) return def;
  if (typeof v === 'number') return Number.isFinite(v) ? v : def;
  const s = String(v).trim();
  if (!s) return def;
  const m = s.match(/-?\d+(?:\.\d+)?/);
  const n = m ? parseFloat(m[0]) : NaN;
  return Number.isFinite(n) ? n : def;
}

function fromDdbNum(v: any, def = 0): number {
  if (v === null || v === undefined) return def;
  if (typeof v === 'number') return Number.isFinite(v) ? v : def;
  if (typeof v === 'string') {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n : def;
  }
  if (typeof v === 'object') {
    if (v.N !== undefined) {
      const n = parseFloat(v.N);
      return Number.isFinite(n) ? n : def;
    }
    if (v.S !== undefined) {
      const n = parseFloat(v.S);
      return Number.isFinite(n) ? n : def;
    }
  }
  return def;
}

function normalizeMd(
  md: any
): { score?: number; motion?: number; structure?: number; keypoints?: number; flow?: number; orient?: number } | null {
  if (!md) return null;
  const src = md.M ?? md;
  return {
    score: fromDdbNum(src.score, 0),
    motion: fromDdbNum(src.motion, 0),
    structure: fromDdbNum(src.structure, 0),
    keypoints: fromDdbNum(src.keypoints, 0),
    flow: fromDdbNum(src.flow, 0),
    orient: fromDdbNum(src.orient, 0),
  };
}

function n(x: any, def = 0): number {
  if (x == null) return def;
  if (typeof x === 'number') return Number.isFinite(x) ? x : def;
  if (typeof x === 'string') {
    const f = parseFloat(x);
    return Number.isFinite(f) ? f : def;
  }
  if (typeof x === 'object') {
    if ('N' in x) return n((x as any).N, def);
    if ('S' in x) return n((x as any).S, def);
    if ('M' in x) return n((x as any).M, def);
  }
  return def;
}

function readMd(raw: any) {
  const src = raw && raw.M ? raw.M : raw;
  if (!src) return null;
  const get = (k: string) => n(src[k], NaN);
  const score = get('score');
  const motion = get('motion');
  const structure = get('structure');
  const keypoints = get('keypoints');
  const flow = get('flow');
  const orient = get('orient');
  const any = [score, motion, structure, keypoints, flow, orient].some(Number.isFinite);
  if (!any) return null;
  return { score, motion, structure, keypoints, flow, orient };
}

function readFlat(p: any) {
  const movement = n(p.movement, NaN);
  const motion = n(p.motion, NaN);
  const structure = n(p.structure, NaN);
  const keypoints = n(p.keypoints, NaN);
  const flow = n(p.flow, NaN);
  const orient = n(p.orient, NaN);
  const any = [movement, motion, structure, keypoints, flow, orient].some(Number.isFinite);
  if (!any) return null;
  return { movement, motion, structure, keypoints, flow, orient };
}

function getTimeMs(p: any): number {
  if (p.time) {
    const t = new Date(p.time).getTime();
    if (Number.isFinite(t)) return t;
  }
  const ca = p.createdAt;
  if (ca != null) {
    const num = n(ca, NaN);
    if (Number.isFinite(num)) {
      return num < 1e12 ? num * 1000 : num;
    }
    const t = new Date(ca).getTime();
    if (Number.isFinite(t)) return t;
  }
  return 0;
}

function toNum(x: any): number {
  if (x == null) return NaN;
  if (typeof x === 'number') return x;
  if (typeof x === 'string') {
    const n = parseFloat(x);
    return Number.isFinite(n) ? n : NaN;
  }
  if (typeof x === 'object') {
    if ('N' in x) {
      const n = parseFloat((x as any).N);
      return Number.isFinite(n) ? n : NaN;
    }
    if ('S' in x) {
      const n = parseFloat((x as any).S);
      return Number.isFinite(n) ? n : NaN;
    }
  }
  return NaN;
}

function getPath(obj: any, path: (string | number)[]): any {
  let cur = obj;
  for (const k of path) {
    if (cur == null) return undefined;
    cur = cur[k as any];
  }
  return cur;
}

function pickNum(obj: any, paths: (string | number)[][], def = 0): number {
  for (const p of paths) {
    const v = getPath(obj, p);
    const n = toNum(v);
    if (Number.isFinite(n)) return n;
  }
  return def;
}

function strOf(x: any): string {
  if (x == null) return '';
  if (typeof x === 'string') return x;
  if (typeof x === 'object' && 'S' in x) return String((x as any).S);
  return String(x);
}

function getTimeMsFlexible(p: any): number {
  const raw = p?.createdAt ?? p?.time ?? p?.timestamp;
  const n = toNum(raw);
  if (Number.isFinite(n)) return n < 1e12 ? n * 1000 : n;
  const t = new Date(raw).getTime();
  return Number.isFinite(t) ? t : 0;
}

function fromAttr(x: any): any {
  if (x == null) return x;
  if (typeof x !== 'object') return x;

  if ('N' in x) {
    const v = parseFloat(x.N);
    return Number.isFinite(v) ? v : 0;
  }
  if ('S' in x) return String(x.S);
  if ('BOOL' in x) return !!x.BOOL;
  if ('M' in x) {
    const out: any = {};
    const m = x.M as Record<string, any>;
    for (const k of Object.keys(m)) out[k] = fromAttr(m[k]);
    return out;
  }
  if ('L' in x) return (x.L as any[]).map(fromAttr);

  const out: any = Array.isArray(x) ? [] : {};
  for (const [k, v] of Object.entries(x)) out[k] = fromAttr(v);
  return out;
}

function firstFinite(...xs: number[]): number {
  for (const v of xs) if (Number.isFinite(v)) return v;
  return 0;
}

function getTimeMsJS(p: any): number {
  if (p.time) {
    const t = new Date(p.time).getTime();
    if (Number.isFinite(t)) return t;
  }
  if (p.createdAt != null) {
    if (typeof p.createdAt === 'number') {
      return p.createdAt < 1e12 ? p.createdAt * 1000 : p.createdAt;
    }
    const t = new Date(p.createdAt).getTime();
    if (Number.isFinite(t)) return t;
  }
  return 0;
}

function scoreFromKpi(k: Partial<Kpi> | undefined): number {
  if (!k) return 0;
  const Sm = Number(k.motion ?? 0);
  const Ss = Number(k.structure ?? 0);
  const Sk = Number(k.keypoints ?? 0);
  const Sf = Number(k.flow ?? 0);
  const So = Number(k.orient ?? 0);
  const s = 0.15 * Sm + 0.28 * Ss + 0.19 * Sk + 0.11 * Sf + 0.28 * So;
  return clamp01(s);
}

const EPS = 1e-6;

function anyPositive(obj: any, paths: (string | number)[][], eps = EPS): boolean {
  for (const p of paths) {
    const v = toNum(getPath(obj, p));
    if (Number.isFinite(v) && v > eps) return true;
  }
  return false;
}

function hasAnalysisData(p: any): boolean {
  const paths: (string | number)[][] = [
    ['movement'],
    ['movement', 'N'],
    ['movement', 'S'],
    ['kpi', 'score'],
    ['kpi', 'motion'],
    ['kpi', 'structure'],
    ['kpi', 'keypoints'],
    ['kpi', 'flow'],
    ['kpi', 'orient'],
    ['motiondetect', 'score'],
    ['motiondetect', 'motion'],
    ['motiondetect', 'structure'],
    ['motiondetect', 'keypoints'],
    ['motiondetect', 'flow'],
    ['motiondetect', 'orient'],
    ['motionDetect', 'score'],
    ['motionDetect', 'motion'],
    ['motionDetect', 'structure'],
    ['motionDetect', 'keypoints'],
    ['motionDetect', 'flow'],
    ['motionDetect', 'orient'],
    ['motion'],
    ['structure'],
    ['keypoints'],
    ['flow'],
    ['orient'],
    ['motion', 'N'],
    ['structure', 'N'],
    ['keypoints', 'N'],
    ['flow', 'N'],
    ['orient', 'N'],
    ['motion', 'S'],
    ['structure', 'S'],
    ['keypoints', 'S'],
    ['flow', 'S'],
    ['orient', 'S'],
  ];
  return anyPositive(p, paths, EPS);
}

async function getSignedUrlPublic(key: string): Promise<string> {
  const clean = stripPublic(key);
  const url = await Storage.get(clean, {
    level: 'public',
    expires: 900,
    cacheControl: 'max-age=60',
  });
  return String(url);
}

@Component
export default class MotionAnalyze extends Vue {
  @Prop({ type: Array, required: true }) readonly photos!: Photo[];
  @Prop({ type: String, required: true }) readonly bucket!: string;
  @Prop({ type: String, required: true }) readonly cameraId!: string;
  @Prop({ type: String, required: true }) readonly apiUrl!: string;
  @Prop({ type: String, required: true }) readonly jwt!: string;

  @Prop({ type: Array, default: () => [] }) readonly initialRoi!: Pt[];
  @Prop({ type: Function, default: null })
  readonly urlResolver!: ((key: string) => Promise<string | null | undefined>) | null;

  @Prop({ type: Array, default: () => [] }) readonly initialResults!: Result[];

  @Prop({ type: Function, default: null })
  readonly cachedResultsResolver!: ((pairs: Pair[], photos: Photo[]) => Promise<Result[]>) | null;

  @Prop({ type: Boolean, default: true }) readonly prefetch!: boolean;

  @Ref('stage') readonly stageRef!: HTMLElement;
  @Ref('baseImg') readonly baseImgRef!: HTMLImageElement;
  @Ref('overlay') readonly overlayRef!: HTMLCanvasElement;
  @Ref('chartActivity') readonly chartActivityRef!: HTMLDivElement;
  @Ref('chartScatter') readonly chartScatterRef!: HTMLDivElement;
  @Ref('media') readonly mediaRef!: HTMLElement;

  @State('user', { namespace: 'profile' }) private user!: User;

  public lastImageUrl = '';
  public imgErrorOnce = false;

  public points: Pt[] = [];
  public normalizedRoi: Pt[] = [];

  public running = false;
  public startedAt = 0;
  public done = 0;
  public total = 0;
  public results: Result[] = [];
  public minMovement = 0.0;

  public plotlyReady = !!window.Plotly;
  public visible = false;

  public prefillBusy = false;

  private _ro: ResizeObserver | null = null;
  private _onKey!: (e: KeyboardEvent) => void;

  public undoEnabled = false;
  private suppressNextPrefill = false;
  private lastNonEmptyRows: Result[] = [];

  public armed = false;
  private lastMinBeforeExclude = 0;

  public selectedIds: string[] = [];
  private selectedPointIdx: number[] = [];
  private _scatterRows: Result[] = [];

  get pendingIds(): string[] {
    return this.armed ? this.belowThresholdIds : [];
  }

  get lastImage(): Photo | null {
    if (!this.photos.length) return null;
    const copy = this.photos.slice().sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
    return copy[copy.length - 1];
  }

  get pairs(): Pair[] {
    if (!this.visible || !this.photos?.length) return [];
    const arr = this.photos.slice().sort((a, b) => +new Date(a.time) - +new Date(b.time));
    const out: Pair[] = [];
    for (let i = 1; i < arr.length; i++) {
      out.push({
        keyA: arr[i - 1].key,
        keyB: arr[i].key,
        photoBId: (arr[i].id || arr[i].key) as string,
        tB: new Date(arr[i].time).getTime(),
      });
    }
    out.sort((a, b) => a.tB - b.tB);
    return out;
  }

  get filteredResults(): Result[] {
    if (!this.visible) return this.lastNonEmptyRows;
    return this.results.filter((r) => (r.movement || 0) >= this.minMovement).sort((a, b) => (a.tB || 0) - (b.tB || 0));
  }

  get progressPct(): number {
    return this.total ? Math.round((100 * this.done) / this.total) : 0;
  }

  get etaText(): string {
    if (!this.running || !this.done) return '--:--';
    const elapsed = (Date.now() - this.startedAt) / 1000;
    const rate = this.done / Math.max(elapsed, 1e-6);
    const remain = this.total - this.done;
    const etaSec = remain / Math.max(rate, 1e-6);
    const m = Math.floor(etaSec / 60),
      s = Math.round(etaSec % 60);
    return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  }

  get quickKpis(): Array<{ key: string; title: string; value: string | number }> {
    if (!this.results.length) return [];
    const m = this.filteredResults.map((r) => r.movement || 0);
    const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / Math.max(xs.length, 1);
    const top = [...m].sort((a, b) => b - a)[0] || 0;
    const med = m.length ? m.slice().sort((a, b) => a - b)[Math.floor(m.length / 2)] : 0;
    return [
      { key: 'count', title: 'Paires analysées', value: this.results.length },
      { key: 'avg', title: 'Moyenne Score', value: `${Math.round(avg(m) * 100)}%` },
      { key: 'median', title: 'Médiane Score', value: `${Math.round(med * 100)}%` },
      { key: 'max', title: 'Max Score', value: `${Math.round(top * 100)}%` },
    ];
  }

  get hasPolygon(): boolean {
    return (this.points?.length ?? 0) > 0 || (this.normalizedRoi?.length ?? 0) > 0;
  }
  get canUndo(): boolean {
    return (this.points?.length ?? 0) > 0;
  }
  get canClose(): boolean {
    return (this.points?.length ?? 0) >= 3;
  }

  get belowThresholdIds(): string[] {
    const visible = this.visiblePhotoIdSet;
    const ids = this.results
      .filter((r) => (r.movement ?? 0) < this.minMovement && visible.has(String(r.id)))
      .map((r) => String(r.id));
    return Array.from(new Set(ids));
  }

  get visiblePhotoIdSet(): Set<string> {
    if (!this.visible || !this.photos?.length) return new Set();
    const ids = this.photos.map((p) => String((p as any).id || (p as any).key)).filter(Boolean);
    return new Set(ids);
  }

  get pairsToAnalyze(): Pair[] {
    const arr = this.photos.slice().sort((a, b) => new Date(a.time).getTime() - new Date(b.time).getTime());
    const out: Pair[] = [];
    for (let i = 1; i < arr.length; i++) {
      const A = arr[i - 1];
      const B = arr[i];
      const idB = String((B.id || B.key) as string);
      const need = !hasAnalysisData(B) && !this.analyzedIdSet.has(idB);
      if (need) {
        out.push({ keyA: A.key, keyB: B.key, photoBId: idB, tB: new Date(B.time).getTime() });
      }
    }
    out.sort((a, b) => a.tB - b.tB);
    return out;
  }

  get analyzedCount(): number {
    return Math.max(0, this.pairs.length - this.pairsToAnalyze.length);
  }

  get analyzedIdSet(): Set<string> {
    const s = new Set<string>();
    for (const r of this.results) {
      const m = Number(r.movement || 0);
      const k = r.kpi || ({} as Kpi);
      if (
        m > EPS ||
        (k.motion ?? 0) > EPS ||
        (k.structure ?? 0) > EPS ||
        (k.keypoints ?? 0) > EPS ||
        (k.flow ?? 0) > EPS ||
        (k.orient ?? 0) > EPS
      ) {
        s.add(String(r.id));
      }
    }
    return s;
  }

  get selectionCount(): number {
    return this.selectedIds.length;
  }

  get quickKpisExtended(): Array<{ key: string; title: string; value: string | number }> {
    if (!this.results.length) return [];

    const m = this.filteredResults.map((r) => r.movement || 0);
    const avg = (xs: number[]) => xs.reduce((a, b) => a + b, 0) / Math.max(xs.length, 1);
    const variance = avg(m.map((v) => (v - avg(m)) ** 2));
    const stddev = Math.sqrt(variance);
    const top = [...m].sort((a, b) => b - a)[0] || 0;
    const med = m.length ? m.slice().sort((a, b) => a - b)[Math.floor(m.length / 2)] : 0;
    const min = Math.min(...m);

    return [
      { key: 'count', title: 'Paires analysées', value: this.results.length },
      { key: 'avg', title: 'Moyenne Score', value: `${Math.round(avg(m) * 100)}%` },
      { key: 'median', title: 'Médiane Score', value: `${Math.round(med * 100)}%` },
      { key: 'max', title: 'Max Score', value: `${Math.round(top * 100)}%` },
      { key: 'min', title: 'Min Score', value: `${Math.round(min * 100)}%` },
      { key: 'stddev', title: 'Écart-type', value: `${(stddev * 100).toFixed(1)}%` },
    ];
  }

  excludeSelection(): void {
    if (!this.selectionCount) return;
    const ids = this.selectedIds.slice(); // copie
    this.$emit('exclude-photos', ids);
    this.selectedIds = []; // réassigne (réactif)
    this.selectedPointIdx = [];
    const scaDiv = this.chartScatterRef;
    if (scaDiv && (window as any).Plotly) {
      (window as any).Plotly.restyle(scaDiv, { selectedpoints: [null] });
    }
  }

  applyThresholdFromSelection(): void {
    if (!this.selectionCount) return;
    const rows = this.results.filter((r) => this.selectedIds.includes(String(r.id)));
    const maxSel = rows.reduce((m, r) => Math.max(m, r.movement || 0), 0);
    const thr = Math.min(1, maxSel + 0.001);
    this.minMovement = Math.round(thr * 1000) / 1000;
    this.armed = true;
  }

  excludeUnderThreshold(): void {
    const ids = this.pendingIds;
    if (!ids.length) return;

    this.suppressNextPrefill = true;
    this.undoEnabled = true;

    this.lastMinBeforeExclude = this.minMovement;
    this.$emit('exclude-photos', ids);
    this.armed = false;
  }

  undoExclude(): void {
    this.$emit('undo-exclude-request');
    this.undoEnabled = false;
    this.armed = false;
  }

  @Watch('initialRoi', { immediate: true, deep: true })
  onInitialRoiChanged(v: Pt[]): void {
    if (v && v.length) {
      this.normalizedRoi = v.map((p) => ({ x: clamp01(p.x), y: clamp01(p.y) }));
      this.syncOverlaySize();
    }
  }

  @Watch('initialResults', { immediate: true, deep: true })
  onInitialResults(v: Result[]): void {
    if (Array.isArray(v) && v.length) {
      this.mergeResults(v, true);
      this.$nextTick(this.renderCharts);
    }
  }

  private _prefillTimer: number | null = null;
  private schedulePrefill(force = false) {
    if (this._prefillTimer) window.clearTimeout(this._prefillTimer);
    this._prefillTimer = window.setTimeout(() => {
      this._prefillTimer = null;
      void this.prefillCachedResults(force);
    }, 120);
  }

  @Watch('photos', { deep: false })
  onPhotosChangedShallow(): void {
    if (!this.visible) return;
    this.schedulePrefill(false);
    this.armed = false;
  }

  private _renderRaf: number | null = null;
  private scheduleRender() {
    if (this._renderRaf) cancelAnimationFrame(this._renderRaf);
    this._renderRaf = requestAnimationFrame(() => {
      this._renderRaf = null;
      if (this.visible) this.renderCharts();
    });
  }

  @Watch('results')
  onResultsChanged(): void {
    if (this.visible) this.scheduleRender();
  }

  @Watch('minMovement')
  onMinMovementChanged(): void {
    this.armed = true;
    if (this.visible) this.scheduleRender();
  }

  mounted(): void {
    this._onKey = (e: KeyboardEvent) => this.onKey(e);
    window.addEventListener('keydown', this._onKey);

    if ((window as any).ResizeObserver) {
      const ro: ResizeObserver = new (window as any).ResizeObserver(() => this.syncOverlaySize());
      this._ro = ro;
      const el = (this.stageRef as unknown as Element) || null;
      if (el) ro.observe(el);
    }
  }

  beforeDestroy(): void {
    window.removeEventListener('keydown', this._onKey);
    if (this._ro) this._ro.disconnect();
  }

  mergeResults(rows: Partial<Result>[] = [], replace = true): void {
    if (!Array.isArray(rows) || !rows.length) return;
    const keyOf = (r: Partial<Result>) =>
      r?.id ?? r?.tB ?? (r as any)?.keyB ?? `${(r as any)?.keyA}->${(r as any)?.keyB}`;
    const map = new Map(this.results.map((r) => [keyOf(r), r]));
    for (const r of rows) {
      const k = keyOf(r);
      if (!map.has(k)) {
        map.set(k, r as Result);
        continue;
      }
      map.set(k, replace ? ({ ...map.get(k), ...r } as Result) : map.get(k)!);
    }
    this.results = Array.from(map.values()).sort((a, b) => (a.tB || 0) - (b.tB || 0));
  }

  resultsFromPhotos(): Result[] {
    const self = this as any;

    const source: any[] =
      (Array.isArray(self.dataImagesKey) && self.dataImagesKey) ||
      (Array.isArray(self.photos) && self.photos) ||
      (Array.isArray(self.filteredPhotos) && self.filteredPhotos) ||
      (Array.isArray(self.items) && self.items) ||
      [];

    const rows = source.map((w: any) => w?.data ?? w);
    rows.sort((a, b) => getTimeMsFlexible(a) - getTimeMsFlexible(b));

    const out: Result[] = [];

    for (let i = 1; i < rows.length; i++) {
      const A = rows[i - 1];
      const B = rows[i];

      const movementDirect = pickNum(
        B,
        [
          ['movement'],
          ['movement', 'N'],
          ['motiondetect', 'score'],
          ['motiondetect', 'M', 'score'],
          ['motionDetect', 'score'],
          ['motionDetect', 'M', 'score'],
          ['kpi', 'score'],
        ],
        0
      );

      const motion = pickNum(
        B,
        [
          ['motion'],
          ['motion', 'N'],
          ['motiondetect', 'motion'],
          ['motiondetect', 'M', 'motion'],
          ['motionDetect', 'motion'],
          ['motionDetect', 'M', 'motion'],
          ['kpi', 'motion'],
        ],
        0
      );

      const structure = pickNum(
        B,
        [
          ['structure'],
          ['structure', 'N'],
          ['motiondetect', 'structure'],
          ['motiondetect', 'M', 'structure'],
          ['motionDetect', 'structure'],
          ['motionDetect', 'M', 'structure'],
          ['kpi', 'structure'],
        ],
        0
      );

      const keypoints = pickNum(
        B,
        [
          ['keypoints'],
          ['keypoints', 'N'],
          ['motiondetect', 'keypoints'],
          ['motiondetect', 'M', 'keypoints'],
          ['motionDetect', 'keypoints'],
          ['motionDetect', 'M', 'keypoints'],
          ['kpi', 'keypoints'],
        ],
        0
      );

      const flow = pickNum(
        B,
        [
          ['flow'],
          ['flow', 'N'],
          ['motiondetect', 'flow'],
          ['motiondetect', 'M', 'flow'],
          ['motionDetect', 'flow'],
          ['motionDetect', 'M', 'flow'],
          ['kpi', 'flow'],
        ],
        0
      );

      const orient = pickNum(
        B,
        [
          ['orient'],
          ['orient', 'N'],
          ['motiondetect', 'orient'],
          ['motiondetect', 'M', 'orient'],
          ['motionDetect', 'orient'],
          ['motionDetect', 'M', 'orient'],
          ['kpi', 'orient'],
        ],
        0
      );

      const hasAnyKpi = [motion, structure, keypoints, flow, orient].some((v) => Number.isFinite(v) && v > 0);
      const movement =
        Number.isFinite(movementDirect) && movementDirect > 0
          ? movementDirect
          : hasAnyKpi
          ? scoreFromKpi({ motion, structure, keypoints, flow, orient })
          : 0;

      out.push({
        id: strOf(B.id ?? B.key),
        keyA: strOf(A.key),
        keyB: strOf(B.key),
        tB: getTimeMsFlexible(B),
        movement,
        kpi: { motion, structure, keypoints, flow, orient },
      });
    }
    return out;
  }

  async prefillCachedResults(force = false): Promise<void> {
    if ((!this.prefetch && !force) || this.prefillBusy || !this.visible) return;
    this.prefillBusy = true;
    try {
      const fromPhotos = this.resultsFromPhotos();
      if (fromPhotos.length) {
        this.mergeResults(fromPhotos, true);
        this.$nextTick(this.renderCharts);
      }
    } finally {
      this.prefillBusy = false;
    }
  }

  async refreshLastImageUrl(): Promise<void> {
    if (!this.visible) return;
    this.imgErrorOnce = false;

    const li = this.lastImage;
    if (!li || !li.key) {
      this.lastImageUrl = '';
      return;
    }

    const rawKey = String(li.key).replace(/^\/+/, '');

    if (isAbsoluteUrl(rawKey)) {
      this.lastImageUrl = rawKey;
      return;
    }

    if (this.urlResolver) {
      try {
        const u = await this.urlResolver(rawKey);
        if (u) {
          this.lastImageUrl = String(u);
          return;
        }
      } catch (e) {
        console.warn('[MotionAnalyze] urlResolver error → fallback Amplify', e);
      }
    }

    try {
      const smallAfter = toSmallAfterExt(rawKey);
      this.lastImageUrl = await getSignedUrlPublic(smallAfter);
      return;
    } catch {
      try {
        this.lastImageUrl = await getSignedUrlPublic(rawKey);
        return;
      } catch {
        try {
          const smallPre = toSmallPreExt(rawKey);
          this.lastImageUrl = await getSignedUrlPublic(smallPre);
          return;
        } catch (eSmall) {
          console.warn('[MotionAnalyze] Tous les essais ont échoué', { rawKey, eSmall });
          this.lastImageUrl = '';
        }
      }
    }
  }

  async onImgError(): Promise<void> {
    if (this.imgErrorOnce) return;
    this.imgErrorOnce = true;

    const li = this.lastImage;
    if (!li || !li.key) return;

    try {
      const smallAfter = toSmallAfterExt(li.key);
      const next = await getSignedUrlPublic(smallAfter);

      if (this.baseImgRef && this.baseImgRef.complete) {
        this.lastImageUrl = '';
        this.$nextTick(() => (this.lastImageUrl = next));
      } else {
        this.lastImageUrl = next;
      }
    } catch (err) {
      try {
        const smallPre = toSmallPreExt(li.key);
        const next2 = await getSignedUrlPublic(smallPre);
        if (this.baseImgRef && this.baseImgRef.complete) {
          this.lastImageUrl = '';
          this.$nextTick(() => (this.lastImageUrl = next2));
        } else {
          this.lastImageUrl = next2;
        }
      } catch (err2) {
        console.warn('[MotionAnalyze] Fallback vignette KO', { err, err2 });
      }
    }
  }

  filename(k: string): string {
    return (k || '').split('/').pop() || '';
  }
  asPct(v: number): string {
    return `${Math.round((v || 0) * 100)}%`;
  }

  /* ===== ROI ===== */
  syncOverlaySize(): void {
    const img = this.baseImgRef;
    const c = this.overlayRef;
    const media = this.mediaRef;
    if (!img || !c || !media) return;

    const iw = Math.round(img.clientWidth);
    const ih = Math.round(img.clientHeight);
    if (!iw || !ih) {
      requestAnimationFrame(() => this.syncOverlaySize());
      return;
    }

    c.width = iw;
    c.height = ih;
    c.style.width = iw + 'px';
    c.style.height = ih + 'px';

    this.redraw();
  }

  toNormalized(pt: Pt): Pt {
    const c = this.overlayRef;
    if (!c) return { x: 0, y: 0 };
    return { x: clamp01(pt.x / c.width), y: clamp01(pt.y / c.height) };
  }

  toCanvas(pt: Pt): Pt {
    const c = this.overlayRef;
    if (!c) return { x: 0, y: 0 };
    return { x: pt.x * c.width, y: pt.y * c.height };
  }

  onCanvasClick(e: MouseEvent): void {
    const c = this.overlayRef;
    if (!c) return;

    const rect = c.getBoundingClientRect();
    const x = (e as any).offsetX ?? e.clientX - rect.left;
    const y = (e as any).offsetY ?? e.clientY - rect.top;

    this.points.push({ x, y });
    this.normalizedRoi = this.points.map(this.toNormalized);
    this.redraw();
  }

  onCanvasDown(e: MouseEvent): void {
    const c = this.overlayRef;
    if (!c) return;
    const rect = c.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    this.points.push({ x, y });
    this.normalizedRoi = this.points.map(this.toNormalized);
    this.redraw();
  }

  closePolygon(): void {
    if ((this.points || []).length >= 3) {
      this.normalizedRoi = this.points.map(this.toNormalized);
      this.redraw(true);
      this.$emit('roi-change', this.normalizedRoi);
    }
  }

  undoPoint(): void {
    if ((this.points || []).length) {
      this.points.pop();
      this.normalizedRoi = this.points.map(this.toNormalized);
      this.redraw();
    }
  }

  clearPolygon(): void {
    this.points = [];
    this.normalizedRoi = [];
    this.redraw();
    this.$emit('roi-change', []);
  }

  onKey(e: KeyboardEvent): void {
    if (e.key === 'u' || e.key === 'Backspace') this.undoPoint();
    if (e.key === 'c') this.clearPolygon();
    if (e.key === 'Enter') this.closePolygon();
  }

  redraw(closed = false): void {
    const c = this.overlayRef;
    if (!c) return;
    const ctx = c.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);

    const pts = this.points && this.points.length ? this.points : (this.normalizedRoi || []).map(this.toCanvas);
    if (!pts.length) return;

    if (closed || (this.normalizedRoi && this.normalizedRoi.length >= 3)) {
      const poly = this.normalizedRoi && this.normalizedRoi.length ? this.normalizedRoi.map(this.toCanvas) : pts;
      ctx.save();
      ctx.beginPath();
      poly.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
      ctx.closePath();
      ctx.fillStyle = 'rgba(255, 225, 0, 0.25)';
      ctx.fill();
      ctx.restore();
    }

    ctx.beginPath();
    pts.forEach((p, i) => (i ? ctx.lineTo(p.x, p.y) : ctx.moveTo(p.x, p.y)));
    if (closed || pts.length >= 3) ctx.closePath();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#00aa55';
    ctx.stroke();

    ctx.fillStyle = '#007a3d';
    pts.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 3, 0, Math.PI * 2);
      ctx.fill();
    });
  }

  /* ===== Pool ===== */
  async runPool<T>(jobs: Array<() => Promise<T>>, limit: number, onProgress?: (done: number) => void): Promise<T[]> {
    const ret: T[] = [];
    let i = 0;
    let completed = 0;
    const workers = new Array(Math.min(limit, jobs.length)).fill(0).map(async () => {
      while (i < jobs.length) {
        const cur = i++;
        const r = await jobs[cur]().catch((err) => ({ error: String(err) } as unknown as T));
        ret[cur] = r;
        completed++;
        if (onProgress) onProgress(completed);
        await Promise.resolve();
      }
    });
    await Promise.all(workers);
    return ret;
  }

  /* ===== Analyse ===== */
  private async _runAnalysisFor(targetPairs: Pair[]): Promise<void> {
    this.running = true;
    this.done = 0;
    this.total = targetPairs.length;
    this.startedAt = Date.now();

    const roi = this.normalizedRoi.map((p) => ({ x: +p.x.toFixed(5), y: +p.y.toFixed(5) }));
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${this.jwt}`,
    };

    const jobs = targetPairs.map((pair) => async (): Promise<Result> => {
      const keyA = ensurePublicPrefix(stripPublic(pair.keyA))!;
      const keyB = ensurePublicPrefix(stripPublic(pair.keyB))!;
      const body = {
        bucket: this.bucket,
        keyA,
        keyB,
        photoBId: pair.photoBId,
        cameraId: this.cameraId,
        polygonPoints: roi,
        debug: true,
      };

      const res = await fetch(this.apiUrl, { method: 'POST', headers, body: JSON.stringify(body) });
      const raw = await res.json().catch(() => ({} as any));

      const payload: any =
        raw && typeof raw.body === 'string'
          ? JSON.parse(raw.body)
          : raw && typeof raw.body === 'object'
          ? raw.body
          : raw;

      const movement = fromDdbNum(payload?.movement ?? payload?.score, 0);
      const md = normalizeMd(
        payload?.kpi ??
          payload?.motiondetect ?? {
            motion: payload?.motion,
            structure: payload?.structure,
            keypoints: payload?.keypoints,
            flow: payload?.flow,
            orient: payload?.orient,
          }
      );

      const kpi: Kpi = {
        motion: md?.motion ?? 0,
        structure: md?.structure ?? 0,
        keypoints: md?.keypoints ?? 0,
        flow: md?.flow ?? 0,
        orient: md?.orient ?? 0,
      };

      const merged: Result = {
        id: pair.photoBId,
        keyA: pair.keyA,
        keyB: pair.keyB,
        tB: pair.tB,
        movement,
        kpi,
        debug: payload?.debug ?? '',
      };

      return merged; // Retourne sans merge/done/schedule ici !
    });

    // Lance le pool avec callback pour progress (léger)
    let allResults: Result[] = [];
    try {
      allResults = await this.runPool(jobs, 50, (prog) => {
        this.done = prog;
        // Render moins souvent pour éviter le spam (ex. tous les 10 jobs)
        if (this.visible && this.plotlyReady && prog % 10 === 0) {
          this.scheduleRender();
        }
      });
    } catch (err) {
      console.error(err);
    }

    this.running = false;
    // Merge tout d'un coup à la fin (rapide)
    this.mergeResults(allResults, true);
    if (this.visible && this.plotlyReady) {
      this.$nextTick(() => this.renderCharts());
    }
  }

  async startAnalysis(): Promise<void> {
    if (!this.normalizedRoi.length) {
      alert('Dessine le ROI d’abord.');
      return;
    }
    const targetPairs = this.pairsToAnalyze;
    if (!targetPairs.length) {
      alert('Rien à analyser : toutes les photos ont déjà des KPI.');
      return;
    }
    await this._runAnalysisFor(targetPairs);
  }

  async startAnalysisAll(): Promise<void> {
    if (!this.normalizedRoi.length) {
      alert('Dessine le ROI d’abord.');
      return;
    }
    if (this.pairs.length < 2) {
      alert('Pas assez de photos pour former des paires.');
      return;
    }
    const go = window.confirm(
      `Relancer toute l’analyse va recalculer les KPI pour ${this.pairs.length - 1} paires. Continuer ?`
    );
    if (!go) return;

    this.results = [];
    await this._runAnalysisFor(this.pairs);
  }

  /* ===== Charts ===== */
  renderCharts(): void {
    if (!this.visible || !this.plotlyReady) return;
    const actDiv = this.chartActivityRef;
    const scaDiv = this.chartScatterRef;
    if (!actDiv || !scaDiv) return; // <-- ne plus vérifier motDiv

    let rows = this.filteredResults;
    if (rows.length === 0) {
      rows = this.lastNonEmptyRows.length ? this.lastNonEmptyRows : [];
      if (rows.length === 0) {
        window.Plotly?.purge(actDiv);
        window.Plotly?.purge(scaDiv); // <-- enlève la purge de motDiv
        return;
      }
    } else {
      this.lastNonEmptyRows = rows.slice();
    }

    const xTimes = rows.map((r) => new Date(r.tB));
    const yMov = rows.map((r) => r.movement || 0);
    const xM = rows.map((r) => r.kpi?.motion ?? 0);
    const yS = rows.map((r) => r.kpi?.structure ?? 0);
    const size = rows.map((r) => 8 + 14 * (r.kpi?.keypoints ?? 0));
    const hoverPairs = rows.map((r) => `${this.filename(r.keyA)} → ${this.filename(r.keyB)}`);

    const paddedRange = (vals: number[], padFrac = 0.05) => {
      const v = vals.filter((n) => Number.isFinite(n));
      if (!v.length) return { min: 0, max: 1 };
      const vmin = Math.min(...v);
      const vmax = Math.max(...v);
      const span = Math.max(1e-9, vmax - vmin);
      const pad = span * padFrac || Math.max(1e-6, Math.abs(vmax || vmin) * 0.02);
      return { min: vmin - pad, max: vmax + pad };
    };

    const yMinRaw = Math.min(...yMov);
    const yMaxRaw = Math.max(...yMov);
    const span = yMaxRaw - yMinRaw;
    const pad = span > 0 ? span * 0.05 : Math.max(1e-6, Math.abs(yMaxRaw || yMinRaw) * 0.02);
    const yMin = yMinRaw - pad;
    const yMax = yMaxRaw + pad;

    const motionYR = paddedRange(xM);
    const scatterXR = paddedRange(xM);
    const scatterYR = paddedRange(yS);

    const commonLayout = {
      paper_bgcolor: 'transparent',
      plot_bgcolor: 'transparent',
      font: { color: '#5fc9e4', family: 'Poppins, sans-serif', size: 12 },
      xaxis: { type: 'date', color: '#5fc9e4', gridcolor: '#1c304a', zerolinecolor: '#2b4a70' },
      yaxis: {
        color: '#5fc9e4',
        gridcolor: '#1c304a',
        zerolinecolor: '#2b4a70',
        autorange: false,
        range: [yMin, yMax],
      },
      margin: { l: 45, r: 20, t: 25, b: 40 },
      hoverlabel: { bgcolor: 'rgba(0,19,38,0.9)', bordercolor: '#5fc9e4', font: { color: '#e8f3f8' } },
      legend: { orientation: 'h', y: 1.1, x: 0.5, xanchor: 'center', font: { color: '#5fc9e4', size: 11 } },
    } as const;

    this._scatterRows = rows;
    this.selectedPointIdx = rows.reduce((acc: number[], r, i) => {
      if (this.selectedIds.includes(String(r.id))) acc.push(i);
      return acc;
    }, []);

    // 1) Score activité
    window.Plotly?.react(
      actDiv,
      [
        // Trace 1 : Score (movement) -> axe Y gauche (par défaut)
        {
          x: xTimes,
          y: yMov,
          mode: 'lines+markers',
          name: 'Score activité',
          line: { width: 2, color: '#5fc9e4' },
          marker: { size: 6 },
          text: hoverPairs,
          hovertemplate: '<b>%{text}</b><br>Score: %{y:.4f}<extra></extra>',
          legendgroup: 'score',
        },
        // Trace 2 : Motion (KPI) -> axe Y droit
        {
          x: xTimes,
          y: xM,
          mode: 'lines+markers',
          name: 'Motion (KPI)',
          line: { width: 2, color: '#7fd1ae' },
          marker: { size: 6 },
          text: hoverPairs,
          hovertemplate: '<b>%{text}</b><br>Motion: %{y:.4f}<extra></extra>',
          yaxis: 'y2',
          legendgroup: 'motion',
        },
      ],
      {
        ...commonLayout,
        // un peu plus d’espace à droite pour l’axe Y2
        margin: { ...commonLayout.margin, r: 60 },
        // Axe gauche = Score (movement)
        yaxis: {
          ...commonLayout.yaxis,
          title: 'Score',
          autorange: false,
          range: [yMin, yMax],
        },
        // Axe droite = Motion
        yaxis2: {
          title: 'Motion',
          overlaying: 'y',
          side: 'right',
          autorange: false,
          range: [motionYR.min, motionYR.max],
          tickfont: { color: '#7fd1ae' },
          titlefont: { color: '#7fd1ae' },
          gridcolor: 'rgba(0,0,0,0)', // pas de grille Y2
          zerolinecolor: '#2b4a70',
        },
        // Couleurs des axes (optionnel : cohérence)
        xaxis: { ...commonLayout.xaxis },
      },
      {
        responsive: true,
        displaylogo: false,
        modeBarButtonsToRemove: ['lasso2d', 'select2d'],
        displayModeBar: true,
      }
    );

    // 3) Scatter Motion vs Structure
    window.Plotly?.react(
      scaDiv,
      [
        {
          x: xM,
          y: yS,
          mode: 'markers',
          name: 'Motion vs Structure',
          marker: {
            size,
            color: yMov,
            colorscale: [
              [0, '#132f47'],
              [0.3, '#0b7294'],
              [0.6, '#3dd9de'],
              [1, '#e8f3f8'],
            ],
            showscale: true,
            colorbar: {
              title: 'Movement',
              tickcolor: '#5fc9e4',
              tickfont: { color: '#5fc9e4' },
              titlefont: { color: '#5fc9e4' },
              bgcolor: 'transparent',
            },
          },
          text: hoverPairs,
          hovertemplate: 'Motion: %{x:.3f}<br>Structure: %{y:.3f}<br>%{text}<extra></extra>',
          selectedpoints: this.selectedPointIdx ?? [],
          selected: { marker: { opacity: 1 } },
          unselected: { marker: { opacity: 0.25 } },
        },
      ],
      {
        ...commonLayout,
        dragmode: 'lasso',
        xaxis: {
          title: 'Motion',
          color: '#5fc9e4',
          gridcolor: '#1c304a',
          autorange: false,
          range: [scatterXR.min, scatterXR.max],
        },
        yaxis: {
          title: 'Structure',
          color: '#5fc9e4',
          gridcolor: '#1c304a',
          autorange: false,
          range: [scatterYR.min, scatterYR.max],
        },
      },
      { responsive: true, displaylogo: false, displayModeBar: true }
    );
    this.attachScatterSelection();
  }

  attachScatterSelection(): void {
    const scaDiv = this.chartScatterRef as any;
    if (!scaDiv || !(window as any).Plotly) return;

    // Nettoie d'abord pour éviter les doublons
    if (typeof scaDiv.removeAllListeners === 'function') {
      scaDiv.removeAllListeners('plotly_selected');
      scaDiv.removeAllListeners('plotly_deselect');
    }

    scaDiv.on('plotly_selected', (ev: any) => {
      const pts = Array.isArray(ev?.points) ? ev.points : [];
      const idxs = pts.map((p: any) => p.pointIndex).filter((i: any) => Number.isFinite(i));
      const ids = idxs
        .map((i: number) => this._scatterRows[i])
        .filter(Boolean)
        .map((r: Result) => String(r.id));

      this.selectedIds = ids; // <-- nouvelle réf (réactif)
      this.selectedPointIdx = idxs; // <-- nouvelle réf
      (window as any).Plotly.restyle(scaDiv, { selectedpoints: [idxs] });
    });

    scaDiv.on('plotly_deselect', () => {
      this.selectedIds = []; // <-- nouvelle réf (réactif)
      this.selectedPointIdx = [];
      (window as any).Plotly.restyle(scaDiv, { selectedpoints: [null] });
    });
  }

  @Watch('visible', { immediate: true })
  async onVisibilityChange(v: boolean) {
    this.$emit(v ? 'motion-open' : 'motion-closed');
    if (!v) {
      if (this._ro) this._ro.disconnect();
      if (this._prefillTimer) {
        window.clearTimeout(this._prefillTimer);
        this._prefillTimer = null;
      }
      if (this._renderRaf) {
        cancelAnimationFrame(this._renderRaf);
        this._renderRaf = null;
      }
      if (window.Plotly) {
        window.Plotly.purge(this.chartActivityRef);
        window.Plotly.purge(this.chartScatterRef);
      }
      return;
    }
    if (!this.plotlyReady) {
      await new Promise<void>((resolve) => {
        if (window.Plotly) return resolve();
        const s = document.createElement('script');
        s.src = 'https://cdn.plot.ly/plotly-2.30.0.min.js';
        s.onload = () => resolve();
        document.head.appendChild(s);
      });
      this.plotlyReady = true;
    }
    await this.refreshLastImageUrl();
    this.schedulePrefill(true); // force un pré-remplissage une seule fois à l’ouverture
    this.$nextTick(() => {
      this.syncOverlaySize();
      window.Plotly?.Plots.resize(this.chartActivityRef);
      window.Plotly?.Plots.resize(this.chartScatterRef);
      this.renderCharts();
    });
  }
}
